import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Shield, AlertTriangle, CheckCircle2, Filter } from 'lucide-react';
import { supabase } from '../services/supabase';

type Summary = {
  days: number;
  since: string;
  generations: {
    total: number;
    by_app: Record<string, number>;
    by_classification: Record<string, number>;
    visible_disclosure: number;
    machine_readable: number;
    provenance_failed: number;
    missing_source: number;
    unknown_provider: number;
  };
  imagemotion_images: {
    total: number;
    hyperdusk: number;
    standard: number;
    by_classification: Record<string, number>;
    visible_disclosure: number;
    machine_readable: number;
    provenance_failed: number;
    missing_source: number;
    unknown_provider: number;
  };
};

function MetricCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'ok' | 'warn' | 'bad';
}) {
  const toneCls =
    tone === 'ok'
      ? 'border-teal-500/30 text-teal-200'
      : tone === 'warn'
        ? 'border-amber-500/30 text-amber-200'
        : tone === 'bad'
          ? 'border-rose-500/30 text-rose-200'
          : 'border-slate-700 text-slate-100';
  return (
    <div className={`rounded-lg border bg-slate-900/50 px-4 py-3 ${toneCls}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ClassBreakdown({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <p className="text-xs text-slate-500">No data</p>;
  return (
    <ul className="space-y-1.5">
      {entries.map(([k, v]) => (
        <li key={k} className="flex justify-between text-xs text-slate-300">
          <span className="font-medium text-slate-200">{k}</span>
          <span className="tabular-nums text-slate-400">{v}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Admin-only AI transparency / compliance overview.
 * Route: /admin/ai-transparency
 */
export default function AdminAITransparencyPanel() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [appFilter, setAppFilter] = useState<string>('all');

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_ai_transparency_summary', {
        p_days: d,
      });
      if (rpcError) throw rpcError;
      setSummary(data as Summary);
    } catch (err: any) {
      setError(err?.message || 'Failed to load AI transparency summary');
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gen = summary?.generations;
  const im = summary?.imagemotion_images;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-teal-300">
            <Shield size={18} />
            <h2 className="text-lg font-semibold text-white">AI Transparency</h2>
          </div>
          <p className="mt-1 text-xs text-slate-400 max-w-xl">
            Compliance overview for PhotoClear, HyperDusk, Virtual Staging, and ImageMotion.
            Designed to support AI transparency requirements — not a legal compliance guarantee.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <Filter size={14} />
            <select
              value={days}
              onChange={(e) => {
                const d = Number(e.target.value);
                setDays(d);
                load(d);
              }}
              className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-slate-200"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => load(days)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-slate-700"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-12 justify-center">
          <Loader2 className="animate-spin" size={18} />
          Loading summary…
        </div>
      )}

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-950/30 px-4 py-3 text-sm text-rose-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Could not load summary</p>
            <p className="text-xs text-rose-300/80 mt-1">{error}</p>
            <p className="text-xs text-slate-400 mt-2">
              Ensure migration <code className="text-slate-300">20260811120000_ai_transparency_provenance.sql</code> is applied.
            </p>
          </div>
        </div>
      )}

      {!loading && summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Generation records" value={gen?.total ?? 0} />
            <MetricCard
              label="Visible disclosure"
              value={gen?.visible_disclosure ?? 0}
              tone="ok"
            />
            <MetricCard
              label="Machine-readable"
              value={gen?.machine_readable ?? 0}
              tone="ok"
            />
            <MetricCard
              label="Provenance failed"
              value={gen?.provenance_failed ?? 0}
              tone={(gen?.provenance_failed ?? 0) > 0 ? 'bad' : 'default'}
            />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MetricCard label="Missing source link" value={gen?.missing_source ?? 0} tone="warn" />
            <MetricCard label="Unknown provider/model" value={gen?.unknown_provider ?? 0} tone="warn" />
            <MetricCard label="ImageMotion / HyperDusk rows" value={im?.total ?? 0} />
            <MetricCard
              label="IM provenance failed"
              value={im?.provenance_failed ?? 0}
              tone={(im?.provenance_failed ?? 0) > 0 ? 'bad' : 'default'}
            />
          </div>

          <div className="flex flex-wrap gap-2 text-xs">
            {['all', 'photoclear', 'staging', 'hyperdusk', 'imagemotion'].map((app) => (
              <button
                key={app}
                type="button"
                onClick={() => setAppFilter(app)}
                className={`rounded-md px-3 py-1.5 border ${
                  appFilter === app
                    ? 'border-teal-500/50 bg-teal-900/30 text-teal-200'
                    : 'border-slate-700 bg-slate-900 text-slate-400 hover:text-slate-200'
                }`}
              >
                {app === 'all' ? 'All apps' : app}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h3 className="text-sm font-semibold text-slate-100 mb-3">PhotoClear & Virtual Staging</h3>
              <p className="text-[11px] text-slate-500 mb-3">From generations table (last {days} days)</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[11px] uppercase text-slate-500 mb-2">By app</h4>
                  <ClassBreakdown
                    data={
                      appFilter === 'all'
                        ? gen?.by_app || {}
                        : { [appFilter]: gen?.by_app?.[appFilter] || 0 }
                    }
                  />
                </div>
                <div>
                  <h4 className="text-[11px] uppercase text-slate-500 mb-2">By classification</h4>
                  <ClassBreakdown data={gen?.by_classification || {}} />
                </div>
              </div>
              <ul className="mt-4 space-y-1 text-xs text-slate-400">
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-teal-400" />
                  PhotoClear: assisted (enhance) vs modified (declutter / sky / fill)
                </li>
                <li className="flex items-center gap-1.5">
                  <CheckCircle2 size={12} className="text-teal-400" />
                  Virtual Staging: typically AI_MODIFIED
                </li>
              </ul>
            </section>

            <section className="rounded-lg border border-slate-800 bg-slate-900/40 p-4">
              <h3 className="text-sm font-semibold text-slate-100 mb-3">HyperDusk & ImageMotion</h3>
              <p className="text-[11px] text-slate-500 mb-3">From imagemotion_project_images</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-[11px] uppercase text-slate-500 mb-2">Service mix</h4>
                  <ClassBreakdown
                    data={{
                      hyperdusk: im?.hyperdusk ?? 0,
                      imagemotion: im?.standard ?? 0,
                    }}
                  />
                </div>
                <div>
                  <h4 className="text-[11px] uppercase text-slate-500 mb-2">By classification</h4>
                  <ClassBreakdown data={im?.by_classification || {}} />
                </div>
              </div>
              <ul className="mt-4 space-y-1 text-xs text-slate-400">
                <li>HyperDusk dusk stills → AI_MODIFIED</li>
                <li>HyperDusk / ImageMotion video → AI_GENERATED</li>
                <li>
                  Visible disclosure: {im?.visible_disclosure ?? 0} · Machine-readable:{' '}
                  {im?.machine_readable ?? 0}
                </li>
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
